import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useFetch, act } from "../../lib/useFetch";
import { LANGS } from "../../lib/constants";
import { Field, Toggle } from "./AdminDialog";
import PasswordForm from "./PasswordForm";

const OFFSETS = [["1d", "за 1 день"], ["3h", "за 3 часа"], ["1h", "за 1 час"]];
const lines = (arr) => (arr || []).join("\n");
const toList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function Card({ title, children }) {
  return <div className="glass rounded-2xl p-6 space-y-4"><h4 className="font-heading text-xl font-bold">{title}</h4>{children}</div>;
}

async function exportDb() {
  try {
    const res = await api.get("/admin/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `arvex-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Экспорт готов");
  } catch {
    toast.error("Не удалось выгрузить базу");
  }
}

export default function SettingsPanel() {
  const [data, reload] = useFetch("/admin/settings");
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm({ ...data.settings, wl: lines(data.settings.web_search_whitelist), bl: lines(data.settings.web_search_blacklist) }); }, [data]);
  if (!form) return <div className="text-gray-400" data-testid="panel-settings">Загрузка…</div>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const toggleOffset = (o) => set("reminder_offsets")(form.reminder_offsets.includes(o) ? form.reminder_offsets.filter((x) => x !== o) : [...form.reminder_offsets, o]);
  const save = async () => {
    const { wl, bl, ...rest } = form;
    const res = await act(api.put("/admin/settings", { ...rest, web_search_whitelist: toList(wl), web_search_blacklist: toList(bl) }), "Настройки сохранены");
    if (res) reload();
  };

  return (
    <div className="space-y-6" data-testid="panel-settings">
      <Card title="🚧 Заглушка сайта">
        <Toggle checked={form.maintenance_enabled} onChange={set("maintenance_enabled")} label="Показывать посетителям страницу «Техническое обслуживание»" testid="settings-maintenance-toggle" />
        <Field label="Текст заглушки"><textarea rows={2} value={form.maintenance_message} onChange={(e) => set("maintenance_message")(e.target.value)} data-testid="settings-maintenance-message" /></Field>
      </Card>
      <Card title="🌐 Язык по умолчанию">
        <select value={form.default_language} onChange={(e) => set("default_language")(e.target.value)} className="!w-auto" data-testid="settings-default-language">
          {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <p className="text-xs text-gray-500">Для новых посетителей, которые ещё не выбирали язык.</p>
      </Card>
      <Card title="⏰ Напоминания о дедлайнах">
        <Toggle checked={form.reminders_enabled} onChange={set("reminders_enabled")} label="Включить напоминания" testid="settings-reminders-toggle" />
        <div className="flex gap-4 flex-wrap">
          {OFFSETS.map(([k, l]) => <Toggle key={k} checked={form.reminder_offsets.includes(k)} onChange={() => toggleOffset(k)} label={l} testid={`settings-reminder-${k}`} />)}
        </div>
        <p className="text-xs text-gray-500">Отправка напоминаний в Telegram подключится в Части 6.6.</p>
      </Card>
      <Card title="🤖 Автономия AI">
        <Toggle checked={form.ai_autonomy_enabled} onChange={set("ai_autonomy_enabled")} label="Разрешить AI менять цены в пределах коридора" testid="settings-ai-autonomy-toggle" />
        <Field label="Макс. изменение цены за раз, %">
          <input type="number" min={0} max={100} value={form.ai_max_price_change_pct} onChange={(e) => set("ai_max_price_change_pct")(Number(e.target.value))} className="!w-32" data-testid="settings-ai-max-pct" />
        </Field>
      </Card>
      <Card title="🔍 Web-поиск">
        <Toggle checked={form.web_search_enabled} onChange={set("web_search_enabled")} label="Разрешить AI искать в интернете" testid="settings-web-search-toggle" />
        <Field label="Лимит запросов в сутки">
          <input type="number" min={0} value={form.web_search_daily_limit} onChange={(e) => set("web_search_daily_limit")(Number(e.target.value))} className="!w-32" data-testid="settings-web-search-limit" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Whitelist (домен в строке)"><textarea rows={3} value={form.wl} onChange={(e) => set("wl")(e.target.value)} data-testid="settings-whitelist" /></Field>
          <Field label="Blacklist (домен в строке)"><textarea rows={3} value={form.bl} onChange={(e) => set("bl")(e.target.value)} data-testid="settings-blacklist" /></Field>
        </div>
      </Card>
      <button onClick={save} className="btn-gold" data-testid="settings-save-btn">Сохранить настройки</button>
      <Card title="🔑 Смена пароля"><PasswordForm /></Card>
      <Card title="📦 Экспорт базы">
        <p className="text-sm text-gray-400">JSON со всеми таблицами (без паролей).</p>
        <button onClick={exportDb} className="btn-outline" data-testid="settings-export-btn">📥 Скачать экспорт</button>
      </Card>
    </div>
  );
}
